import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'

/**
 * A recursive helper function to find an item by its ID in the tree.
 * @param {Array} items - The array of items to search.
 * @param {String} id - The ID of the item to find.
 * @returns {Object|null} - The found item or null.
 */
function findItemRecursive(items, id) {
  for (const item of items) {
    if (item.id === id) {
      return item
    }
    if (item.children) {
      const found = findItemRecursive(item.children, id)
      if (found) {
        return found
      }
    }
  }
  return null
}


function canAccessItem(item, authStore) {
  return !item.permission || authStore.hasPermission(item.permission)
}

function menuOrder(item) {
  return item.order ?? item.sortOrder ?? 0
}

function buildSortedTree(items, authStore) {
  if (!items || items.length === 0) {
    return []
  }

  // 1. Sort the current level
  const sortedItems = [...items]
    .filter(item => canAccessItem(item, authStore))
    .sort((a, b) => menuOrder(a) - menuOrder(b))

  // 2. Recursively build children and check for expansion
  return sortedItems.reduce((visibleItems, item) => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0
    const newChildren = buildSortedTree(item.children, authStore);

    if (hasChildren && newChildren.length === 0) {
      return visibleItems
    }

    // 3. Check if any child is expanded
    // We use 'isExpanded' here because the children have already been processed
    const hasExpandedChild = newChildren.some(child => child.isExpanded === true);

    // 4. Determine if this item should be expanded
    // It's expanded if it's set to 'expanded: true' *or* if it has a child that is.
    const isExpanded = item.expanded === true || hasExpandedChild;

    visibleItems.push({
      ...item,
      children: newChildren,
      isExpanded: isExpanded, // Add this new, calculated prop
    })

    return visibleItems
  }, [])
}

export const useLeftMenuStore = defineStore('leftMenu', {
  /**
   * State: The raw, unsorted array of top-level menu items.
   * Item structure: { id, title, href, order, children: [...] }
   */
  state: () => ({
    menuItems: [],
  }),

  /**
   * Getters: Provide the sorted, reactive menu to your components.
   */
  getters: {
    /**
     * Returns the entire menu tree, deeply sorted by 'order'.
     * This is what your components will use.
     */
    sortedMenu: (state) => {
      return buildSortedTree(state.menuItems, useAuthStore())
    },
  },

  /**
   * Actions: Used to add or modify menu items from anywhere in the app.
   */
  actions: {
    /**
     * Injects a new top-level menu item.
     * @param {Object} item - The menu item to add (e.g., { id: 'dashboard', title: 'Dashboard', href: '/', order: 10 })
     */
    addTopLevelItem(item) {
      this.menuItems.push(item)
    },

    /**
     * Injects a child item into an existing parent item, found by parentId.
     * @param {String} parentId - The unique ID of the parent item.
     * @param {Object} childItem - The child item to add (e.g., { id: 'settings-profile', title: 'Profile', href: '/profile', order: 10 })
     */
    addChildItem(parentId, childItem) {
      // Find the parent item anywhere in the tree
      const parent = findItemRecursive(this.menuItems, parentId)

      if (parent) {
        // Ensure the children array exists
        if (!parent.children) {
          parent.children = []
        }
        parent.children.push(childItem)
      } else {
        console.warn(`[MenuStore] Could not find parent with ID "${parentId}" to add child item.`)
      }
    },

    /**
     * Utility to set the entire initial menu, e.g., on app load.
     * @param {Array} items - The full menu tree.
     */
    setMenu(items) {
      this.menuItems = items
    },
  },
})
